import { useEffect, useState } from 'react';
import ErrorBanner from '../../components/ErrorBanner';
import Modal from '../../components/Modal';
import {
  createSubcategory,
  deleteSubcategory,
  listSubcategories,
  updateSubcategory,
} from '../../api/subcategories';

export default function SubcategoriesSection({ token }) {
  const [subcategories, setSubcategories] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [name, setName] = useState('');
  const [nameTa, setNameTa] = useState('');
  const [nameThanglish, setNameThanglish] = useState('');
  const [formError, setFormError] = useState('');
  const [isSaving, setIsSaving] = useState(false);

  const load = async () => {
    setIsLoading(true);
    setError('');
    try {
      setSubcategories(await listSubcategories(token));
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const openCreate = () => {
    setEditing(null);
    setName('');
    setNameTa('');
    setNameThanglish('');
    setFormError('');
    setModalOpen(true);
  };

  const openEdit = (subcategory) => {
    setEditing(subcategory);
    setName(subcategory.name);
    setNameTa(subcategory.name_ta || '');
    setNameThanglish(subcategory.name_thanglish || '');
    setFormError('');
    setModalOpen(true);
  };

  const closeModal = () => setModalOpen(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    setFormError('');
    try {
      const payload = {
        name,
        name_ta: nameTa || null,
        name_thanglish: nameThanglish || null,
      };
      if (editing) {
        await updateSubcategory(token, editing.subcategory_id, payload);
      } else {
        await createSubcategory(token, payload);
      }
      closeModal();
      await load();
    } catch (err) {
      setFormError(err.message);
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async (subcategory) => {
    if (!window.confirm(`Delete subcategory "${subcategory.name}"?`)) return;
    try {
      await deleteSubcategory(token, subcategory.subcategory_id);
      await load();
    } catch (err) {
      setError(err.message);
    }
  };

  return (
    <section>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-semibold text-gray-900">Subcategories</h2>
        <button
          onClick={openCreate}
          className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-medium text-white hover:bg-indigo-700"
        >
          + New subcategory
        </button>
      </div>

      <p className="text-sm text-gray-500 mb-4">
        Each song can be tagged with one subcategory (e.g. Hymns, Chorus) at the time it's
        created or edited, from the Songs section.
      </p>

      <ErrorBanner message={error} onRetry={load} />

      <div className="bg-white border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 text-sm">
          <thead className="bg-gray-50">
            <tr>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Name (English)</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Name (Tamil)</th>
              <th className="text-left px-4 py-2 font-medium text-gray-500">Name (Thanglish)</th>
              <th className="text-right px-4 py-2 font-medium text-gray-500">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {isLoading ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                  Loading...
                </td>
              </tr>
            ) : subcategories.length === 0 ? (
              <tr>
                <td colSpan={4} className="px-4 py-6 text-center text-gray-400">
                  No subcategories yet
                </td>
              </tr>
            ) : (
              subcategories.map((sc) => (
                <tr key={sc.subcategory_id}>
                  <td className="px-4 py-2 text-gray-900">{sc.name}</td>
                  <td className="px-4 py-2 text-gray-600">{sc.name_ta || '—'}</td>
                  <td className="px-4 py-2 text-gray-600">{sc.name_thanglish || '—'}</td>
                  <td className="px-4 py-2 text-right space-x-3 whitespace-nowrap">
                    <button
                      onClick={() => openEdit(sc)}
                      className="text-gray-600 hover:underline"
                    >
                      Edit
                    </button>
                    <button
                      onClick={() => handleDelete(sc)}
                      className="text-red-600 hover:underline"
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        </div>
      </div>

      {modalOpen && (
        <Modal
          title={editing ? `Edit ${editing.name}` : 'New subcategory'}
          onClose={closeModal}
        >
          <form onSubmit={handleSubmit} className="space-y-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name (English)
              </label>
              <input
                required
                value={name}
                onChange={(e) => setName(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name (Tamil) — optional
              </label>
              <input
                value={nameTa}
                onChange={(e) => setNameTa(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Name (Thanglish) — optional
              </label>
              <input
                value={nameThanglish}
                onChange={(e) => setNameThanglish(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>
            {formError && <p className="text-sm text-red-600">{formError}</p>}
            <button
              type="submit"
              disabled={isSaving}
              className="w-full rounded-md bg-indigo-600 px-3 py-2 text-sm font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </form>
        </Modal>
      )}
    </section>
  );
}
